import * as XLSX from "xlsx";

/**
 * Excel preview processor utility
 */
class ExcelPreviewProcessor {
  /**
   * Extract sheet names and create a preview of the Excel file
   *
   * @param {File} file - The Excel file
   * @returns {Promise<Object>} Information about the Excel file and its sheets
   */
  static async extractSheetInfo(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "array" });

          const sheetNames = workbook.SheetNames;

          const sheetsPreview = {};

          for (const sheetName of sheetNames) {
            const worksheet = workbook.Sheets[sheetName];

            const preview = XLSX.utils
              .sheet_to_json(worksheet, { header: 1, range: 0, defval: "" })
              .slice(0, 5);

            const headers = preview.length > 0 ? preview[0] : [];

            const requiredColumns = [
              "Year",
              "Country_specific_Energy_Target",
              "Baseline_Emission",
              "Decarbonization_Target_Scenario_1",
              "Asset_Energy_Intensity",
              "Climate_And_Grid_Corrected_Asset_Performance",
            ];

            const headerMap = {};
            for (const header of headers) {
              if (typeof header === "string") {
                const lowerHeader = header.toLowerCase();

                // Map headers to required columns (fuzzy match)
                if (lowerHeader.includes("year")) {
                  headerMap["Year"] = header;
                } else if (
                  lowerHeader.includes("country") &&
                  lowerHeader.includes("energy") &&
                  lowerHeader.includes("target")
                ) {
                  headerMap["Country_specific_Energy_Target"] = header;
                } else if (
                  lowerHeader.includes("baseline") &&
                  lowerHeader.includes("emission")
                ) {
                  headerMap["Baseline_Emission"] = header;
                } else if (
                  lowerHeader.includes("decarbon") &&
                  lowerHeader.includes("target")
                ) {
                  headerMap["Decarbonization_Target_Scenario_1"] = header;
                } else if (
                  lowerHeader.includes("asset") &&
                  lowerHeader.includes("intensity")
                ) {
                  headerMap["Asset_Energy_Intensity"] = header;
                } else if (
                  lowerHeader.includes("climate") &&
                  lowerHeader.includes("grid")
                ) {
                  headerMap["Climate_And_Grid_Corrected_Asset_Performance"] =
                    header;
                }
              }
            }

            const missingColumns = requiredColumns.filter(
              (col) =>
                !Object.values(headerMap).some(
                  (header) =>
                    header &&
                    header
                      .toLowerCase()
                      .includes(col.toLowerCase().split("_")[0])
                )
            );

            sheetsPreview[sheetName] = {
              headers,
              preview: preview.slice(1), // Remove headers from preview
              headerMap,
              missingColumns,
              isValid: missingColumns.length === 0,
            };
          }

          resolve({
            fileName: file.name,
            fileSize: file.size,
            sheetNames,
            sheetsPreview,
          });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsArrayBuffer(file);
    });
  }
}

export default ExcelPreviewProcessor;
